/**
 * FAQMenu — 카테고리별 자주 찾는 질문 카드 (1단계)
 *
 * FAQItem은 두 종류:
 * - type: 'intent'   → 클릭 시 onSelectIntent (해당 Q&A 답변 출력)
 * - type: 'submenu'  → 클릭 시 onSelectSubmenu (하위 메뉴 카드 출력)
 *
 * 데이터: frontend/src/data/faq-menu.json (인증 상태별 분기)
 */

import { cn } from '@/lib/utils';
import type { FAQCategory } from '@/types/qa';

interface FAQMenuProps {
  categories: FAQCategory[];
  onSelectIntent: (intent: string, label: string) => void;
  onSelectSubmenu: (submenuId: string) => void;
}

export function FAQMenu({ categories, onSelectIntent, onSelectSubmenu }: FAQMenuProps) {
  return (
    <div className="ml-flex ml-flex-col ml-gap-3 ml-mt-2">
      {categories.map((cat) => (
        <CategoryCard
          key={cat.category_id}
          category={cat}
          onSelectIntent={onSelectIntent}
          onSelectSubmenu={onSelectSubmenu}
        />
      ))}
    </div>
  );
}

interface CategoryCardProps {
  category: FAQCategory;
  onSelectIntent: (intent: string, label: string) => void;
  onSelectSubmenu: (submenuId: string) => void;
}

function CategoryCard({ category, onSelectIntent, onSelectSubmenu }: CategoryCardProps) {
  return (
    <div
      className={cn(
        'ml-bg-white ml-border ml-border-line ml-rounded-cb-md',
        'ml-overflow-hidden'
      )}
    >
      <div
        className={cn(
          'ml-flex ml-items-center ml-gap-2 ml-px-3 ml-py-2',
          'ml-bg-surface ml-border-b ml-border-line-soft'
        )}
      >
        <span className="ml-text-base ml-leading-none">{category.category_icon}</span>
        <span className="ml-text-cb-sm ml-font-semibold ml-text-ink">
          {category.category_label}
        </span>
      </div>

      <div className="ml-flex ml-flex-col">
        {category.items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              if (item.type === 'submenu') {
                onSelectSubmenu(item.submenu_id);
              } else {
                onSelectIntent(item.intent, item.label);
              }
            }}
            className={cn(
              'ml-flex ml-items-center ml-gap-2',
              'ml-px-3 ml-py-2.5 ml-text-left',
              'ml-text-cb-base ml-text-ink-secondary ml-cursor-pointer',
              'ml-transition-colors ml-duration-150',
              'hover:ml-bg-brand-50 hover:ml-text-brand-600',
              idx > 0 && 'ml-border-t ml-border-line-soft',
              item.highlight && 'ml-bg-brand-50/50 ml-text-brand-600 ml-font-medium'
            )}
          >
            <span className="ml-text-mute ml-text-cb-sm">▸</span>
            <span className="ml-flex-1 ml-min-w-0 ml-truncate">{item.label}</span>
            {item.subtitle && (
              <span className="ml-text-cb-xs ml-text-mute ml-flex-shrink-0">
                {item.subtitle}
              </span>
            )}
            {item.type === 'submenu' && (
              <span className="ml-text-mute ml-text-cb-sm ml-flex-shrink-0">›</span>
            )}
            {item.highlight && (
              <span className="ml-text-cb-xs ml-text-accent ml-flex-shrink-0">⭐</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
